import React, { useCallback, useState } from 'react';
import ModalComponent from './modal/modalComponent';
import InputComponent from './inputComponent';
import { useGetComponent } from '../hooks/useGetComponent';
import { componentIds } from '../utilities/constants';
import { ModalButtonProps } from './modal/modal.interfaces';

interface AgreementProviderModalProps {
  handleAccepted: () => Promise<unknown> | void;
  loadingAccepted: boolean;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AgreementProviderModal: React.FC<AgreementProviderModalProps> = ({
  isOpen,
  setIsOpen,
  loadingAccepted,
  handleAccepted,
}) => {
  const { loading, data: locale } = useGetComponent({
    componentId: componentIds.MODAL_USER_AGREEMENT,
    locale: 'en',
  });

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const handleCheckboxChange = (checked: boolean) => {
    setIsCheckboxChecked(checked);
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const Buttons: ModalButtonProps[] = [
    {
      label: locale?.accepted,
      onClick: handleAccepted,
      disabled: !isCheckboxChecked || loadingAccepted,
      testID: 'accepted',
      className: '!text-sm !font-bold !rounded !py-2.5 !leading-[20px] w-auto',
    },
    {
      label: locale?.cancel,
      onClick: onClose,
      type: 'underline',
      className: '!text-sm !font-bold',
      testID: 'close-modal',
    },
  ];

  if (loading || !locale) {
    return null;
  }

  return (
    <ModalComponent
      buttons={Buttons}
      title={locale.title}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="text-med-gray-3 h-[630px] overflow-auto -mr-10 pr-10 font-medium flex flex-col gap-30">
        <p className="text-sm">{locale.descriptions}</p>
        <div className="flex flex-col gap-5">
          <h4 className="text-h4 font-semibold text-neutral-800">
            {locale?.firstPoint.title}
          </h4>
          <p>{locale?.firstPoint.description}</p>
          <div className="flex flex-col gap-2.5">
            <h5 className="text-h5 font-semibold text-neutral-800">
              {locale?.firstPoint.subtitlesList[0]}
            </h5>
            <p>{locale?.firstPoint.subTextsList[0]}</p>
          </div>
          <div className="flex flex-col gap-2.5">
            <h5 className="text-h5 font-semibold text-neutral-800">
              {locale?.firstPoint.subtitlesList[1]}
            </h5>
            <p>{locale?.firstPoint.subTextsList[1]}</p>
            <ul className="list-disc pl-6 marker:text-neutral-800 space-y-2.5 marker:text-sm">
              {locale?.firstPoint.definitionsPoints.map(
                (item: Record<string, string>) => (
                  <li key={item.title}>
                    <span className="font-semibold text-neutral-800 mr-1">
                      {item.title}
                    </span>
                    {item.text}
                  </li>
                ),
              )}
            </ul>
          </div>
          <div className="flex flex-col gap-2.5">
            <h5 className="text-h5 font-semibold text-neutral-800">
              {locale?.firstPoint.subtitlesList[2]}
            </h5>
            <p>{locale?.firstPoint.subTextsList[2]}</p>
          </div>
        </div>

        {locale?.points.map(
          (point: Record<string, string | Array<Record<string, string>>>) => (
            <div className="flex flex-col gap-5">
              <h4 className="text-h4 font-semibold text-neutral-800">
                {point.title as string}
              </h4>
              <p>{point.text as string}</p>
              {(point.subtitles as Array<Record<string, string>>).map(
                (item: Record<string, string>) => (
                  <div className="flex flex-col gap-2.5">
                    <h5 className="text-h5 font-semibold text-neutral-800">
                      {item.subtitle}
                    </h5>
                    <p>{item.text}</p>
                  </div>
                ),
              )}
            </div>
          ),
        )}

        <div className="flex flex-col gap-5">
          <h4 className="text-h4 font-semibold text-neutral-800">
            {locale?.lastPoint.title}
          </h4>
          <p>{locale?.lastPoint.description}</p>
          {locale?.lastPoint.subtitles.map(
            (item: Record<string, string | string[]>) => (
              <div className="flex flex-col gap-2.5">
                <h5 className="text-h5 font-semibold text-neutral-800">
                  {item.subtitle}
                </h5>
                <p>{item.text}</p>
                <ul className="list-disc pl-6 marker:text-med-gray-3 space-y-2.5 marker:text-sm">
                  {(item.list as string[])?.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              </div>
            ),
          )}
          <p>{locale?.anyViolationOfTheseProhibitions}</p>
        </div>
        <div className="flex flex-row gap-2.5 items-start mt-8">
          <InputComponent
            testID="policy-input"
            type="checkbox"
            name="agreementsCheckbox"
            checkboxProps={{
              checked: isCheckboxChecked,
              onCheckboxChange: handleCheckboxChange,
              isAlternativeCheckboxColor: true,
              checkboxLabel: (
                <div className="text-med-gray-3 text-sm font-semibold">
                  <p>{locale?.acknowledge}</p>
                </div>
              ),
            }}
          />
        </div>
        <p className="text-sm">{locale?.onceYouHaveAcknowledge}</p>
      </div>
    </ModalComponent>
  );
};

export default AgreementProviderModal;
